/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */
import { DefaultEventsMap } from '@socket.io/component-emitter';
import { useEffect, useState, useRef, FC } from 'react';
import { Socket } from 'socket.io-client';

import EventCaptureOverlay from '@/components/sessionRecorder/EventCaptureOverlay';
import HighlighterOverlay from '@/components/sessionRecorder/HighlighterOverlay';
import { stripProtocol } from '@/lib/utils';

type LiveAgentProps = {
    runId: string;
    debugPageId: string;
    socket: Socket<DefaultEventsMap, DefaultEventsMap>;
    isRecording: boolean;
    mode: 'record' | 'highlight';
    panelOpen: boolean;
    viewportDimensions?: { width: number; height: number };
};

const MAX_REFRESHES = 5;
let refreshCount = 0;

export const LiveAgentPlayer: FC<LiveAgentProps> = ({
    runId,
    debugPageId,
    socket,
    isRecording,
    mode = 'record',
    panelOpen = false,
    viewportDimensions = { width: 1280, height: 720 },
}: LiveAgentProps) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const wsProtocol = process.env.NODE_ENV !== 'development' ? 'wss' : 'ws';
    const [iframeSrc, setIframeSrc] = useState<string>('');

    // Update iframe source URL when debugPageId changes
    useEffect(() => {
        if (!debugPageId) return;

        const wsUrl =
            process.env.NODE_ENV !== 'development' || import.meta.env.VITE_CLUSTER_SETUP === 'true'
                ? `${stripProtocol(import.meta.env.VITE_CLUSTER_URL)}/${runId}/debug/devtools/page/${debugPageId}`
                : `localhost:9222/devtools/page/${debugPageId}`;

        const src = `https://screencast-${panelOpen ? 'with-devtools' : 'chi'}.vercel.app/inspector.html?${wsProtocol}=${wsUrl}`;
        setIframeSrc(src);

        // Reset refresh count when debugPageId changes
        refreshCount = 0;
    }, [debugPageId, runId, panelOpen, wsProtocol]);

    const [selectElement, setSelectElement] = useState<{
        hid: string;
        options: { text: string; value: string; selected: boolean; disabled: boolean }[];
    }>({ hid: '', options: [] });

    useEffect(() => {
        if (socket) {
            socket.on('selectOption', (data) => {
                if (data) {
                    setSelectElement(data);
                }
            });
            socket.on('cursorStyle', (style) => {
                document.documentElement.style.setProperty('--cursor-style', style);
                // send a message to the iframe to update the cursor style
                if (iframeRef.current) {
                    iframeRef.current.contentWindow?.postMessage({ type: 'cursorStyle', style }, '*');
                }
            });
        }

        return () => {
            if (socket) {
                socket.off('selectOption');
                socket.off('cursorStyle');
            }
        };
    }, [socket]);

    useEffect(() => {
        // while refreshes are less than MAX_REFRESHES, refresh the iframe when getting targetDisconnected message
        const handleMessage = (event: MessageEvent) => {
            if (event.data.type === 'screencastDisconnected') {
                if (refreshCount < MAX_REFRESHES) {
                    refreshCount++;
                    // eslint-disable-next-line no-console
                    console.log(
                        `Websocket connection lost, trying to reconnect... (${refreshCount} / ${MAX_REFRESHES})`,
                    );
                    if (iframeRef.current) {
                        iframeRef.current.src = iframeSrc;
                    }
                }
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [iframeSrc]);

    return (
        <div
            style={{
                ...(panelOpen
                    ? { height: '100%' }
                    : {
                          aspectRatio: viewportDimensions.width / viewportDimensions.height,
                          maxHeight: 'calc(100% - 28px)',
                      }),
            }}
            className="overflow-hidden mx-auto rounded-b-sm w-full"
        >
            <div className="relative h-full">
                {iframeSrc && (
                    <iframe
                        src={iframeSrc}
                        className="w-full h-full border-0"
                        style={{ cursor: 'var(--cursor-style)' }}
                        title="Inspector"
                        allowFullScreen
                        ref={iframeRef}
                        allow="clipboard-read; clipboard-write *"
                    />
                )}
                {!panelOpen && (
                    <div
                        className="top-0 bottom-0 left-[50%] translate-x-[-50%] absolute"
                        style={{
                            aspectRatio: `${viewportDimensions.width / viewportDimensions.height}`,
                        }}
                    >
                        {isRecording && mode === 'record' && (
                            <EventCaptureOverlay
                                socket={socket}
                                panelOpen={panelOpen}
                                viewportDimensions={viewportDimensions}
                            />
                        )}
                        {mode === 'highlight' && (
                            <HighlighterOverlay
                                socket={socket}
                                panelOpen={panelOpen}
                                viewportDimensions={viewportDimensions}
                            />
                        )}
                        {selectElement && selectElement.options.length > 0 && (
                            <>
                                <select
                                    style={{
                                        position: 'absolute',
                                        top: 'var(--cursor-y)',
                                        left: 'var(--cursor-x)',
                                        transform: 'translate(-50%, -50%)',
                                        opacity: 0,
                                    }}
                                    onMouseMove={(e) => {
                                        e.stopPropagation();
                                    }}
                                    onMouseEnter={() => {
                                        document.body.style.overflow = 'hidden';
                                    }}
                                    onMouseLeave={() => {
                                        document.body.style.overflow = 'auto';
                                    }}
                                    onChange={(e) => {
                                        if (socket) {
                                            socket.emit('remoteControl', {
                                                eventType: 'select',
                                                event: {
                                                    selectedIndex: e.target.selectedIndex,
                                                    hid: selectElement.hid,
                                                },
                                            });
                                        }
                                    }}
                                >
                                    {selectElement.options.map(({ text, value, selected, disabled }) => (
                                        <option key={value} value={value} selected={selected} disabled={disabled}>
                                            {text}
                                        </option>
                                    ))}
                                </select>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
